/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's copyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

export const REFERRAL_STORAGE_KEY = 'housien_referral';
export const REFERRAL_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

interface ReferralData {
  code: string;
  capturedAt: number;
}

/**
 * Access the real (unwrapped) localStorage to bypass the CDU consent wrapper.
 * Referral codes are functional data required for attribution, not tracking cookies,
 * so they must persist across page reloads regardless of consent status.
 */
function getRealLocalStorage(): Storage {
  return (window as any).__originalLocalStorage || localStorage;
}

/**
 * @param capturedAt Timestamp (ms) when the referral was captured.
 * @returns `true` when the referral is older than {@link REFERRAL_TTL_MS}.
 */
function isExpired(capturedAt: number): boolean {
  return Date.now() - capturedAt > REFERRAL_TTL_MS;
}

/**
 * Reads and validates the stored referral entry from real localStorage.
 *
 * @returns The parsed referral data, or `null` when absent/malformed.
 */
function readStored(): ReferralData | null {
  try {
    const raw = getRealLocalStorage().getItem(REFERRAL_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed.code || typeof parsed.code !== 'string') return null;
    return parsed as ReferralData;
  } catch {
    return null;
  }
}

/**
 * Resolves the query string to parse, preferring an explicit override over
 * `window.location.search` (safer for router/mobile edge cases).
 *
 * @param searchOverride Optional query string to use instead of the address bar.
 * @returns The query string to parse.
 */
function resolveSearch(searchOverride?: string): string {
  if (searchOverride !== undefined && searchOverride !== null) return searchOverride;
  return typeof window !== 'undefined' ? window.location.search : '';
}

/**
 * Reads `?ref=` from the current URL and persists it to localStorage.
 * Last-touch-wins: always overwrites the stored referral with the new one.
 *
 * @param searchOverride - Optional query string (e.g. from React Router `location.search`).
 *   When provided, avoids relying on `window.location` alone (mobile / in-memory router edge cases).
 */
export function captureReferralCode(searchOverride?: string): void {
  try {
    const params = new URLSearchParams(resolveSearch(searchOverride));
    const ref = params.get('ref')?.trim();
    if (!ref) return;

    const data: ReferralData = { code: ref, capturedAt: Date.now() };
    getRealLocalStorage().setItem(REFERRAL_STORAGE_KEY, JSON.stringify(data));
  } catch {
    // localStorage unavailable or full -- silently ignore
  }
}

/**
 * Removes the stored referral entry, ignoring any storage errors.
 */
function removeReferralSafe(): void {
  try { getRealLocalStorage().removeItem(REFERRAL_STORAGE_KEY); } catch { /* noop */ }
}

/**
 * Returns the stored referral code, or null if absent/expired.
 * Cleans up expired or malformed entries.
 */
export function getReferralCode(): string | null {
  const stored = readStored();
  if (!stored || isExpired(stored.capturedAt)) {
    removeReferralSafe();
    return null;
  }
  return stored.code;
}

/**
 * Resolves referral for registration payloads: persisted value first, then `?ref=` on the
 * current URL. Used so mobile/OAuth flows still send `referral_code` if storage capture missed
 * but the address bar (or router) still carries `ref`.
 */
export function getReferralCodeForRegistration(): string | undefined {
  const stored = getReferralCode();
  if (stored) return stored;
  try {
    const ref = new URLSearchParams(
      typeof window !== 'undefined' ? window.location.search : ''
    )
      .get('ref')
      ?.trim();
    return ref || undefined;
  } catch {
    return undefined;
  }
}

/**
 * Removes the referral code from localStorage.
 * Called after successful early-access registration.
 */
export function clearReferralCode(): void {
  try {
    getRealLocalStorage().removeItem(REFERRAL_STORAGE_KEY);
  } catch {
    // silently ignore
  }
}
